import { condenseChanges } from './condense-changes.helpers';
import { repairChangeForProposalActionStorage } from './sanitize-proposal-action-changes.helpers';
import { sortProposalChangesWithRecipeFirst } from './proposal-change-order.helpers';
import { createAction } from './action.factory';
import {
  compactActions,
  dedupeCategoryEnrichChangesWithinAction,
  dedupeRedundantResolvedWhenOverridePresentWithinAction,
  dedupeSameSlotSameTypeChangesWithinAction,
  dropNoOpChangesAgainstBaseline,
  dropProposalCountClearChanges,
  getProposalActionOverlapKeySet,
  isRedundantFormulaQuantityOverrideResave,
  compactProposalActionsWhenResolvedMatchesBaseline,
  dropIncomingCategoryEnrichOnBaselinePropertyRevert,
  dropRedundantChangesFromStoredProposalActions,
  getProposalOnlyElementIds,
  pruneSubsumedOverlapChangesInProposalActions,
  stripCategoryRemovedPropertyEditsFromProposalActions,
  stripObsoleteCategoryEnrichOnBaselinePropertyRevert,
  stripStaleCategoryEnrichFromProposalActions,
  subtractOverlappingChangesFromProposalActions,
  type DropNoOpQuantityDisplayHint,
} from './clean-actions.helpers';
import { resolveVersionForProposal } from './resolve-version-for-proposal.helpers';
import { getResolvedBuildingVersion } from './resolved-version.helpers';
import {
  normalizeResolvedVersionEnrichContext,
  type ResolvedVersionEnrichContext,
} from './resolved-version-enrich-cache.helpers';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import type { ProjectChange } from '@shared/helpers/pipeline/update-engine.models';
import type { Baseline, Project } from '@shared/models/project.model';
import type { ProposalId } from '@shared/models/proposals.model';

export type BuildProposalAppendActionPatchOptions = {
  /**
   * When set, no-op checks use {@link getResolvedBuildingVersion} (same enriched tree as the
   * element list UI) instead of proposal-only {@link resolveVersionForProposal} (DEV-3313).
   */
  enrichContext?: ResolvedVersionEnrichContext | Project;
  /** Quantity cell state from the EL row when append runs in the client (DEV-3313). */
  displayHints?: ReadonlyArray<DropNoOpQuantityDisplayHint>;
};

type ProposalLocation = {
  version: Baseline;
  proposalIndex: number;
};

/**
 * Finds the version that owns a {@link IProposal} with the given id and its index in `proposals`.
 */
const findProposalLocation = (
  project: Project,
  proposalId: ProposalId,
): ProposalLocation | undefined => {
  for (const version of project.baselines) {
    if (!version) continue;
    const proposals = version.proposals;
    if (!proposals?.length) continue;
    const proposalIndex = proposals.findIndex((p) => p.id === proposalId);
    if (proposalIndex >= 0) return { version, proposalIndex };
  }
  return undefined;
};

/**
 * Builds the `{ actions, proposals }` patch produced when appending a new {@link Action}
 * to a {@link IProposal} on the supplied version. Returns `undefined` when nothing
 * meaningful should be appended (no changes after no-op/condense, missing proposal, …).
 *
 * Pipeline:
 * 1. {@link condenseChanges} — merge redundant entries inside this batch.
 * 2. {@link stripStaleCategoryEnrichFromProposalActions} — when the batch changes a
 *    category-driving property without a new `ElementCategoryEnrich`, drop prior enrich
 *    rows and edits to properties those rows had added (before subtract so enrich payloads
 *    are still readable).
 * 2b. {@link stripObsoleteCategoryEnrichOnBaselinePropertyRevert} — when the batch includes
 *    a category property edit that matches baseline (e.g. concrete quality back to C30),
 *    drop stale enrich rows that still swap the product child (DEV-3283).
 * 3. {@link subtractOverlappingChangesFromProposalActions} — remove from the proposal's
 *    existing actions any change whose slot is overridden by the new batch. Runs before
 *    the baseline-noop drop so e.g. an `ElementActivate` that matches an active baseline
 *    still removes a prior `ElementDeactivate` action from this proposal.
 * 4. {@link stripCategoryRemovedPropertyEditsFromProposalActions} — when the batch includes
 *    `ElementCategoryEnrich`, drop prior edits to property names in `removePropertyNames`
 *    (baseline can still list those rows).
 * 5. {@link compactActions} — drop emptied actions and unlink them from `proposals`.
 * 6. {@link pruneSubsumedOverlapChangesInProposalActions} — if multiple actions on the
 *    proposal still touch the same overlap slot (older edits), drop those changes from
 *    older actions so the newest action wins; then compact again.
 * 7. {@link dropProposalCountClearChanges} then {@link dropNoOpChangesAgainstBaseline} —
 *    if the cleared slot is owned by a proposal action, drop the clear (revert to
 *    baseline). If the value is baseline, keep the clear so inherit-from-parent is stored.
 *    Remaining no-ops against baseline and stale element ids are dropped (except
 *    `ElementActivate`).
 * 8. Append a new {@link Action} when something is left, run prune+compact once more on the
 *    merged version, otherwise return the cleanup patch when subtract removed prior actions,
 *    or `undefined` when nothing changed.
 */
export const buildProposalAppendActionPatch = (
  version: Baseline,
  proposalId: ProposalId,
  changes: ProjectChange[],
  options?: BuildProposalAppendActionPatchOptions,
): Pick<Baseline, 'actions' | 'proposals'> | undefined => {
  if (changes.length === 0) return undefined;

  const orderedChanges = sortProposalChangesWithRecipeFirst(changes);

  const proposals = version.proposals;
  if (!proposals?.length) return undefined;

  const proposalIndex = proposals.findIndex((p) => p.id === proposalId);
  if (proposalIndex < 0) return undefined;

  const { changes: condensed } = condenseChanges(orderedChanges, { version });
  const dedupedCondensed = dropIncomingCategoryEnrichOnBaselinePropertyRevert(
    version,
    dedupeSameSlotSameTypeChangesWithinAction(
      dedupeRedundantResolvedWhenOverridePresentWithinAction(
        dedupeCategoryEnrichChangesWithinAction(condensed),
      ),
    ),
  );
  if (dedupedCondensed.length === 0) return undefined;

  if (
    isRedundantFormulaQuantityOverrideResave(
      version,
      proposalId,
      dedupedCondensed,
      options?.displayHints,
    )
  ) {
    return undefined;
  }

  // Subtract overlap from previous actions on this proposal, then drop any actions emptied
  // by that subtraction (also unlinks them from proposals[*].actionIds). Done before the
  // baseline-noop drop so reverting a prior action (e.g. Activate after Deactivate) still
  // takes effect even when the new change matches the baseline value.
  // Run before subtract so we can read addProperties from enrich rows we are about to drop.
  let cleanedVersion = stripStaleCategoryEnrichFromProposalActions(
    version,
    proposalId,
    dedupedCondensed,
  );
  cleanedVersion = stripObsoleteCategoryEnrichOnBaselinePropertyRevert(
    cleanedVersion,
    proposalId,
    dedupedCondensed,
  );
  // Capture overlap keys before subtract: a clear of a proposal-owned slot reverts to
  // baseline, while a clear of a baseline value must still be stored.
  const proposalOverlapKeys = getProposalActionOverlapKeySet(
    cleanedVersion,
    proposalId,
  );
  cleanedVersion = subtractOverlappingChangesFromProposalActions(
    cleanedVersion,
    proposalId,
    dedupedCondensed,
  );
  cleanedVersion = stripCategoryRemovedPropertyEditsFromProposalActions(
    cleanedVersion,
    proposalId,
    dedupedCondensed,
  );
  cleanedVersion = compactActions(cleanedVersion);
  cleanedVersion = compactActions(
    pruneSubsumedOverlapChangesInProposalActions(cleanedVersion),
  );

  const proposal = cleanedVersion.proposals?.[proposalIndex];
  if (!proposal) return undefined;

  // Proposal-only structural element ids (e.g. children added by a proposal-scoped
  // {@link ProjectChangeType.ElementRecipeApply}, DEV-3293) live on the resolved view but not on
  // baseline. Pass them through so the no-op pruner doesn't mistake legit child edits for
  // stale references and silently drop them.
  const resolvedBeforeAppend = options?.enrichContext
    ? getResolvedBuildingVersion(
        cleanedVersion,
        proposal,
        normalizeResolvedVersionEnrichContext(options.enrichContext),
      )
    : resolveVersionForProposal(cleanedVersion, proposal).version;
  const filteredAgainstBaseline = dropNoOpChangesAgainstBaseline(
    version,
    dropProposalCountClearChanges(dedupedCondensed, proposalOverlapKeys),
    getProposalOnlyElementIds(cleanedVersion),
    resolvedBeforeAppend,
    options?.displayHints,
  );

  // Partition should never route baseline-only rows here; repair defensively (legacy DB rows).
  const proposalSafeChanges = filteredAgainstBaseline.flatMap((change) => {
    const repaired = repairChangeForProposalActionStorage(change);
    return repaired ? [repaired] : [];
  });

  if (proposalSafeChanges.length === 0) {
    // Nothing new to store on this proposal, but subtract may still have cleaned up prior
    // actions (e.g. an Activate that matches baseline removed a prior Deactivate). Also drop
    // stored overrides that only restate baseline defaults (e.g. carbon factor P50).
    let withoutRedundant =
      dropRedundantChangesFromStoredProposalActions(cleanedVersion);
    withoutRedundant = compactProposalActionsWhenResolvedMatchesBaseline(
      withoutRedundant,
      proposalId,
    );
    if (withoutRedundant !== cleanedVersion) {
      return {
        actions: withoutRedundant.actions ?? [],
        proposals: withoutRedundant.proposals ?? [],
      };
    }
    if (cleanedVersion === version) return undefined;
    return {
      actions: cleanedVersion.actions ?? [],
      proposals: cleanedVersion.proposals ?? [],
    };
  }

  const newAction = createAction({ changes: proposalSafeChanges });

  const nextProposals = [...(cleanedVersion.proposals ?? [])];
  nextProposals[proposalIndex] = {
    ...proposal,
    actionIds: [...proposal.actionIds, newAction.id],
  };

  let merged: Baseline = {
    ...cleanedVersion,
    actions: [...(cleanedVersion.actions ?? []), newAction],
    proposals: nextProposals,
  };
  merged = compactActions(pruneSubsumedOverlapChangesInProposalActions(merged));
  merged = compactProposalActionsWhenResolvedMatchesBaseline(
    merged,
    proposalId,
  );

  return {
    actions: merged.actions ?? [],
    proposals: merged.proposals ?? [],
  };
};

/**
 * Apply new changes to a proposal by appending a new {@link Action}. Thin convenience wrapper
 * around {@link buildProposalAppendActionPatch} for callers that don't go through
 * {@link applyProjectChanges} (and the {@link ProjectChangeType.ProposalAppendAction} change).
 *
 * @param project - Current project snapshot
 * @param proposalId - Target {@link IProposal} id
 * @param changes - Changes to store on the new action (not applied to baseline elements here)
 * @returns Updated project, or the same reference if there is nothing to append
 */
export const applyProjectChangesToProposal = (
  project: Project,
  proposalId: ProposalId,
  changes: ProjectChange[],
  options?: BuildProposalAppendActionPatchOptions,
): Project => {
  if (changes.length === 0) return project;

  const location = findProposalLocation(project, proposalId);
  if (!location) {
    throw new Error(
      `applyProjectChangesToProposal: no proposals entry with id "${proposalId}"`,
    );
  }

  const patch = buildProposalAppendActionPatch(
    location.version,
    proposalId,
    changes,
    { enrichContext: project, ...options },
  );
  if (!patch) return project;

  return updateBuildingVersionProperties(project, {
    id: location.version.id,
    ...patch,
  });
};
